import React, { useCallback, useEffect, useRef, useState } from "react";
import { advisorConsoleApi } from "./advisorConsoleApi";
import "./AdvisorConsole.css";

const LIST_POLL_INTERVAL_MS = 8000;
const MESSAGE_POLL_INTERVAL_MS = 5000;

function TickIcon({ seen }) {
  return (
    <span className={`advisor-console__ticks ${seen ? "advisor-console__ticks--seen" : "advisor-console__ticks--sent"}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12l5 5L15 8" />
        <path d="M9 12l5 5L23 8" />
      </svg>
    </span>
  );
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatListTimestamp(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

export default function AdvisorConsole() {
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const lastMessageIdRef = useRef(0);
  const messagesEndRef = useRef(null);
  const listPollTimerRef = useRef(null);
  const messagePollTimerRef = useRef(null);

  const selectedConversation = conversations.find((c) => c.id === selectedId) || null;

  // ---- Conversation list: load + poll ----
  const loadConversations = useCallback(() => {
    advisorConsoleApi
      .getConversations({ search: search || undefined, unread: unreadOnly ? "1" : undefined })
      .then((res) => setConversations(res.data.conversations || []))
      .catch(() => {
        // Transient errors are swallowed here; next poll tick retries.
      });
  }, [search, unreadOnly]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    listPollTimerRef.current = setInterval(() => {
      if (document.visibilityState === "visible") loadConversations();
    }, LIST_POLL_INTERVAL_MS);
    return () => clearInterval(listPollTimerRef.current);
  }, [loadConversations]);

  // ---- Selected conversation: load messages, then poll for new ones ----
  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }
    setLoadingMessages(true);
    lastMessageIdRef.current = 0;
    advisorConsoleApi
      .getMessages(selectedId, 0)
      .then((res) => {
        const loaded = res.data.messages || [];
        setMessages(loaded);
        const last = loaded[loaded.length - 1];
        lastMessageIdRef.current = last ? last.id : 0;
      })
      .finally(() => setLoadingMessages(false));
  }, [selectedId]);

  const pollMessages = useCallback(() => {
    if (!selectedId) return;
    advisorConsoleApi
      .getMessages(selectedId, lastMessageIdRef.current)
      .then((res) => {
        const incoming = res.data.messages || [];
        if (incoming.length === 0) return;
        setMessages((prev) => [...prev, ...incoming]);
        lastMessageIdRef.current = incoming[incoming.length - 1].id;
      })
      .catch(() => {});
  }, [selectedId]);

  useEffect(() => {
    messagePollTimerRef.current = setInterval(() => {
      if (document.visibilityState === "visible") pollMessages();
    }, MESSAGE_POLL_INTERVAL_MS);
    return () => clearInterval(messagePollTimerRef.current);
  }, [pollMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ---- Mark learner messages as seen once the advisor is caught up ----
  useEffect(() => {
    if (!selectedId || messages.length === 0) return;
    const lastId = messages[messages.length - 1].id;
    advisorConsoleApi.markSeen(selectedId, lastId).then(() => {
      setConversations((prev) =>
        prev.map((c) => (c.id === selectedId ? { ...c, unread_count: 0 } : c))
      );
    }).catch(() => {});
  }, [selectedId, messages]);

  function handleSend() {
    const body = input.trim();
    if (!body || !selectedId || sending) return;

    setSending(true);
    setSendError(null);
    advisorConsoleApi
      .sendMessage(selectedId, body)
      .then((res) => {
        setMessages((prev) => [...prev, res.data]);
        lastMessageIdRef.current = res.data.id;
        setInput("");
        loadConversations();
      })
      .catch((err) => {
        const data = err?.response?.data;
        setSendError(data?.detail || "Couldn't send that message — please try again.");
      })
      .finally(() => setSending(false));
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="advisor-console">
      <aside className="advisor-console__sidebar">
        <div className="advisor-console__sidebar-header">
          <h2>Advisor Chat</h2>
          <input
            className="advisor-console__search"
            type="text"
            placeholder="Search learners…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="advisor-console__unread-toggle">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            Unread only
          </label>
        </div>

        <div className="advisor-console__conversation-list">
          {conversations.length === 0 && (
            <div className="advisor-console__empty-list">No conversations found.</div>
          )}
          {conversations.map((c) => (
            <button
              key={c.id}
              className={`advisor-console__conversation-item ${c.id === selectedId ? "advisor-console__conversation-item--active" : ""}`}
              onClick={() => setSelectedId(c.id)}
            >
              <span className="advisor-console__avatar">{initials(c.user_display_name)}</span>
              <span className="advisor-console__conversation-info">
                <span className="advisor-console__conversation-name-row">
                  <span className="advisor-console__conversation-name">{c.user_display_name}</span>
                  <span className="advisor-console__conversation-time">
                    {formatListTimestamp(c.last_message?.created_at)}
                  </span>
                </span>
                <span className="advisor-console__conversation-preview-row">
                  <span className="advisor-console__conversation-preview">
                    {c.is_banned ? "Banned" : c.last_message?.body || "No messages yet"}
                  </span>
                  {c.unread_count > 0 && (
                    <span className="advisor-console__unread-badge">{c.unread_count}</span>
                  )}
                </span>
              </span>
            </button>
          ))}
        </div>
      </aside>

      <section className="advisor-console__panel">
        {!selectedConversation ? (
          <div className="advisor-console__placeholder">Select a conversation to view messages.</div>
        ) : (
          <>
            <div className="advisor-console__panel-header">
              <span className="advisor-console__avatar">{initials(selectedConversation.user_display_name)}</span>
              <div>
                <div className="advisor-console__panel-name">{selectedConversation.user_display_name}</div>
                <div className="advisor-console__panel-email">{selectedConversation.user_email}</div>
              </div>
              {selectedConversation.is_banned && (
                <span className="advisor-console__banned-pill">Banned</span>
              )}
            </div>

            <div className="advisor-console__messages">
              {loadingMessages && <div className="advisor-console__loading">Loading…</div>}
              {!loadingMessages && messages.length === 0 && (
                <div className="advisor-console__empty-list">No messages in this conversation yet.</div>
              )}
              {messages.map((m) => {
                if (m.sender_type === "system") {
                  return (
                    <div key={m.id} className="advisor-console__bubble-row" style={{ alignSelf: "center" }}>
                      <div className="advisor-console__bubble advisor-console__bubble--system">{m.body}</div>
                    </div>
                  );
                }
                const own = m.sender_type === "advisor";
                return (
                  <div
                    key={m.id}
                    className={`advisor-console__bubble-row ${own ? "advisor-console__bubble-row--own" : "advisor-console__bubble-row--learner"}`}
                  >
                    <div className="advisor-console__bubble">
                      {m.is_flagged && <div className="advisor-console__flag">Flagged: {m.flag_reason}</div>}
                      {m.body}
                    </div>
                    <div className="advisor-console__meta">
                      <span>{formatTime(m.created_at)}</span>
                      {own && <TickIcon seen={m.status === "seen"} />}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="advisor-console__composer">
              {sendError && <div className="advisor-console__warning">{sendError}</div>}
              {selectedConversation.is_banned ? (
                <div className="advisor-console__banned-notice">
                  This learner is banned from advisor chat — you can't reply here.
                </div>
              ) : (
                <div className="advisor-console__input-row">
                  <textarea
                    className="advisor-console__textarea"
                    placeholder="Reply to this learner…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                  />
                  <button className="advisor-console__send" onClick={handleSend} disabled={!input.trim() || sending}>
                    Send
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}